import {Banner, Button, IconButton} from '@wandb/components';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import copyToClipboard from 'copy-to-clipboard';
import React, {memo, useCallback, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal} from 'semantic-ui-react';

import {GetTaskComponent, TaskState} from '../generated/graphql';
import {ProjectPicker} from './ProjectPicker/ProjectPicker';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

interface RunMoverProps {
  entityName: string;
  requireSameEntity?: boolean;
  requireSameEntityReason?: string;
  projectInternalId: string;
  projectName: string;
  activeRunsCount?: number;
  selectedCount: number;
  refetch(): void;
  onMove(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string>;
}

const RunMoverComp: React.FC<RunMoverProps> = ({
  entityName,
  requireSameEntity,
  requireSameEntityReason,
  projectInternalId,
  projectName,
  activeRunsCount = 0,
  selectedCount,
  refetch,
  onMove,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [popupOpen, setPopupOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [destinationEntityName, setDestinationEntityName] = useState<
    string | undefined
  >();
  const [destinationProjectName, setDestinationProjectName] = useState<
    string | undefined
  >();
  const [inProgressTaskID, setInProgressTaskID] = useState<
    string | undefined
  >();
  const [errorTaskID, setErrorTaskID] = useState<string | undefined>();
  const [errorResult, setErrorResult] = useState<string | undefined>();

  const copyTaskID = useCallback(() => {
    if (errorTaskID) {
      copyToClipboard(errorTaskID);
      toast('Task ID copied to clipboard');
    }
  }, [errorTaskID]);

  let button: React.ReactNode;

  if (inProgressTaskID) {
    button = (
      <RunMoverProgress
        inProgressTaskID={inProgressTaskID!}
        onFinished={() => {
          setInProgressTaskID(undefined);
          refetch();
        }}
        onError={(taskID, result) => {
          setErrorTaskID(taskID);
          setErrorResult(result);
          setInProgressTaskID(undefined);
          setErrorOpen(true);
        }}
      />
    );
  } else {
    button = (
      <Button
        isActive={popupOpen}
        startIcon="folder-project-move"
        variant="secondary"
        onPress={() => {
          window.analytics?.track('Move button clicked', {
            location: analyticsLocation,
          });
        }}>
        Move to project
      </Button>
    );
  }

  if (selectedCount === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <ProjectPicker
        entityName={entityName}
        requireSameEntity={requireSameEntity}
        requireSameEntityReason={requireSameEntityReason}
        projectId={projectInternalId}
        projectName={projectName}
        onOpen={() => setPopupOpen(true)}
        onClose={() => setPopupOpen(false)}
        setProject={(entity, proj) => {
          setDestinationEntityName(entity);
          setDestinationProjectName(proj);
          setModalOpen(true);
        }}
        triggerOverride={button}
      />
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setPopupOpen(false);
        }}>
        <Modal.Content>
          <p>
            {`Are you sure you want to move ${selectedCount} ${
              selectedCount === 1 ? 'run' : 'runs'
            } to project `}
            <b>{`${destinationEntityName!}/${destinationProjectName!}`}</b>?
          </p>
          {activeRunsCount > 0 && (
            <Banner variant="warning" isDismissible={false}>
              <Banner.Body>
                {`${activeRunsCount} selected ${activeRunsCount === 1 ? 'run is' : 'runs are'} still active. Moving active runs may cause data loss or logging errors.`}
              </Banner.Body>
            </Banner>
          )}
          <p className="disclaimer">
            Note: Artifacts associated with the runs will not be moved.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            mr={4}
            onPress={() => {
              onMove(destinationEntityName!, destinationProjectName!).then(
                taskID => {
                  setInProgressTaskID(taskID);
                  setModalOpen(false);
                  setPopupOpen(false);
                }
              );
            }}>
            Move runs
          </Button>
          <Button
            variant="ghost"
            data-test="run-move-popup"
            onPress={() => {
              setModalOpen(false);
              setPopupOpen(false);
            }}>
            Nevermind
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal open={errorOpen}>
        <Modal.Content>
          <p>There was an error moving some of your runs:</p>
          {errorResult && (
            <div className="mb-12 rounded px-12 py-8">{errorResult}</div>
          )}
          <p className="mt-12">
            Please try again, and if this keeps happening, contact support and
            share the following task info:
          </p>
          <div className="flex items-center rounded-lg bg-moon-100 p-8 font-mono">
            <span className="flex-1">{errorTaskID}</span>
            <IconButton
              icon="copy"
              variant="ghost"
              aria-label="Copy to clipboard"
              onPress={copyTaskID}
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onPress={() => setErrorOpen(false)}>Ok</Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  );
};

export const RunMover = memo(RunMoverComp);

interface RunMoverProgressProps {
  inProgressTaskID: string;
  onFinished: () => void;
  onError: (taskID: string, result?: string) => void;
}

const RunMoverProgress: React.FC<RunMoverProgressProps> = React.memo(
  ({inProgressTaskID, onFinished, onError}) => {
    const [progressText, setProgressText] = useState('Moving...');

    return (
      <GetTaskComponent
        pollInterval={500}
        fetchPolicy="no-cache"
        notifyOnNetworkStatusChange
        onCompleted={({task}) => {
          const {state, progress, result} = task!;

          setProgressText(`Moving... (${progress}%)`);

          if (state === TaskState.Finished) {
            onFinished();
          } else if (state === TaskState.Failed) {
            onError(inProgressTaskID, result ?? undefined);
          }
        }}
        onError={() => onError(inProgressTaskID)}
        variables={{
          id: inProgressTaskID,
        }}>
        {() => {
          return (
            <Button isDisabled startIcon="folder-project-move" variant="ghost">
              {progressText}
            </Button>
          );
        }}
      </GetTaskComponent>
    );
  }
);
